import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { 
  fetchTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  submitTemplateToMeta,
  setFilters,
  setCurrentTemplate,
  clearCurrentTemplate,
  syncTemplate,
  sendTestMessage
} from '../store/slices/templatesSlice';
import TemplateModal from '../components/TemplateModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import './Templates.css';

const Templates = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Redux selectors
  const { 
    templates, 
    loading, 
    error, 
    filters,
    currentTemplate
  } = useSelector(state => state.templates);
  
  // Local UI state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isSendTestModalOpen, setIsSendTestModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');

  // Fetch templates on mount
  useEffect(() => {
    dispatch(fetchTemplates({ 
      category: filters.category !== 'all' ? filters.category : undefined,
      status: filters.status !== 'all' ? filters.status : undefined,
      search: filters.search || undefined
    })).catch(err => console.error('Failed to fetch templates:', err));
  }, [dispatch, filters]);

  // Handle create template
  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  // Handle edit template
  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  // Handle delete template
  const handleDeleteTemplate = (template) => {
    setTemplateToDelete(template);
    setIsDeleteModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  
  // Handle template save
  const handleTemplateSave = async (formData) => {
    try {
      let result;
      if (editingTemplate) {
        // Update existing template
        result = await dispatch(updateTemplate({
          id: editingTemplate._id,
          data: formData
        })).unwrap();
        alert('✓ Template updated successfully!');
      } else {
        // Create new template
        result = await dispatch(createTemplate(formData)).unwrap();
        
        // Show appropriate message based on server response
        const message = result?.message || 'Template created successfully!';
        const status = result?.data?.status || result?.status;
        
        if (status === 'PENDING') {
          alert(`✓ ${message}\n\nTemplate has been submitted to Meta for approval.`);
        } else if (status === 'DRAFT') {
          alert(`✓ ${message}`);
        } else {
          alert(`✓ ${message}`);
        }
      }
      handleModalClose();
      
      // Refresh templates list
      dispatch(fetchTemplates({ 
        category: filters.category !== 'all' ? filters.category : undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        search: filters.search || undefined
      }));
    } catch (err) {
      console.error('Failed to save template:', err);
      const errorMessage = err?.message || err?.error || 'Failed to save template';
      alert(`✗ Error: ${errorMessage}`);
      throw err; // Re-throw so TemplateModal can display the error
    }
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (templateToDelete) {
      try {
        await dispatch(deleteTemplate(templateToDelete._id)).unwrap();
        setIsDeleteModalOpen(false);
        setTemplateToDelete(null);
      } catch (err) {
        console.error('Failed to delete template:', err);
      }
    }
  };

  // Handle submit to Meta
  const handleSubmitToMeta = async (template) => {
    try {
      const accessToken = localStorage.getItem('whatsappAccessToken');
      const wabaId = localStorage.getItem('wabaId');
      
      if (!accessToken || !wabaId) {
        console.error('WhatsApp credentials missing');
        return;
      }

      await dispatch(submitTemplateToMeta({
        templateId: template._id,
        accessToken,
        wabaId
      })).unwrap();
    } catch (err) {
      console.error('Failed to submit template to Meta:', err);
    }
  };

  // Handle view template details
  const handleViewTemplate = (template) => {
    navigate(`/templates/${template._id || template.id}`);
  };

  // Handle sync template
  const handleSyncTemplate = async (template) => {
    try {
      const metaAccessToken = localStorage.getItem('whatsappAccessToken');
      const appAccessToken = localStorage.getItem('accessToken');
      
      if (!metaAccessToken) {
        alert('WhatsApp access token not found. Please re-authenticate.');
        return;
      }

      if (!appAccessToken) {
        alert('Application access token not found. Please re-login.');
        return;
      }

      console.log(`[Sync Template] Starting sync for template: ${template.name} (${template._id})`);
      
      const result = await dispatch(syncTemplate({
        id: template._id,
        accessToken: metaAccessToken
      })).unwrap();
      
      console.log('[Sync Template] Success:', result);
      alert(`✓ Template "${template.name}" synchronized successfully!\nStatus: ${result.status || 'Updated'}`);
    } catch (err) {
      console.error('[Sync Template] Error:', err);
      const errorMessage = err?.message || err?.error || 'Unknown error';
      const details = err?.details ? `\n\nDetails: ${err.details}` : '';
      alert(`✗ Sync failed: ${errorMessage}${details}`);
    }
  };

  // Handle send test message
  const handleSendTestClick = (template) => {
    setViewingTemplate(template);
    setIsSendTestModalOpen(true);
  };

  // Handle send test message submission
  const handleSendTestMessage = async () => {
    if (!testPhoneNumber.trim()) {
      alert('Please enter a phone number');
      return;
    }

    try {
      const metaAccessToken = localStorage.getItem('whatsappAccessToken');
      const appAccessToken = localStorage.getItem('accessToken');
    

      if (!appAccessToken) {
        alert('Application access token missing. Please re-login.');
        return;
      }

      

      // Phone number is already in E.164 format from react-phone-input-2
      const formattedNumber = testPhoneNumber;

      // Validate phone number has minimum digits
      if (testPhoneNumber.replace(/\D/g, '').length < 10) {
        alert('Phone number too short. Please include country code.');
        return;
      }

    

      // Dispatch Redux thunk
      const result = await dispatch(sendTestMessage({
        templateId: viewingTemplate._id,
        recipientPhone: formattedNumber,
        variableValues: []
      })).unwrap();

      console.log('[Send Test Message] Success:', result);
      
      const messageId = result?.data?.message_id || result?.messageId;
      const successMsg = messageId 
        ? `✓ Test message sent successfully!\nMessage ID: ${messageId}`
        : '✓ Test message sent successfully!';
      
      alert(successMsg);
      setIsSendTestModalOpen(false);
      setTestPhoneNumber('');
    } catch (err) {
      console.error('[Send Test Message] Error:', err);
      const errorMessage = err?.message || err?.error || 'Unknown error occurred';
      alert(`✗ Failed to send test message:\n${errorMessage}`);
    }
  };

  // Handle search
  const handleSearchChange = (value) => {
    dispatch(setFilters({ search: value }));
  };

  // Handle category filter
  const handleCategoryChange = (value) => {
    dispatch(setFilters({ category: value }));
  };

  // Handle status filter
  const handleStatusChange = (value) => {
    dispatch(setFilters({ status: value }));
  };

  const categories = ['all', 'MARKETING', 'UTILITY', 'AUTHENTICATION'];
  const statuses = ['all', 'APPROVED', 'PENDING', 'REJECTED'];
  const filteredTemplates = templates; // Filtering handled by API via Redux

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status, rejectionReason) => {
    const statusConfig = {
      APPROVED: { label: 'Approved', color: '#10b981', bg: '#d1fae5' },
      PENDING: { label: 'Pending Review', color: '#f59e0b', bg: '#fef3c7' },
      REJECTED: { label: 'Rejected', color: '#ef4444', bg: '#fee2e2' },
      DRAFT: { label: 'Draft', color: '#6b7280', bg: '#f3f4f6' }
    };

    const config = statusConfig[status] || { label: status || 'Unknown', color: '#6b7280', bg: '#f3f4f6' };
    return (
      <div className="status-container">
        <span 
          className="status-badge" 
          style={{ 
            background: config.bg, 
            color: config.color 
          }}
        >
          {config.label}
        </span>
        {rejectionReason && (
          <div className="rejection-reason">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{rejectionReason}</span>
          </div>
        )}
      </div>
    );
  };

  const extractVariables = (text) => {
    if (!text) return [];
    const matches = text.match(/\{\{(\d+)\}\}/g);
    return matches ? matches.map(m => m.replace(/[{}]/g, '')) : [];
  };

  const replaceVariablesWithNames = (text, variables) => {
    if (!text || !variables || variables.length === 0) return text;
    let result = text;
    variables.forEach((varName, index) => {
      const varNumber = index + 1;
      const displayName = typeof varName === 'object' ? (varName.displayText || varName.name || varNumber) : varName;
      const regex = new RegExp(`\\{\\{${varNumber}\\}\\}`, 'g');
      result = result.replace(regex, `{{${displayName}}}`);
    });
    return result;
  };

  const getQualityBadge = (qualityScore) => {
    if (!qualityScore) return null;
    const qualityConfig = {
      GREEN: { label: 'High Quality', color: '#10b981', bg: '#d1fae5' },
      YELLOW: { label: 'Medium Quality', color: '#f59e0b', bg: '#fef3c7' },
      RED: { label: 'Low Quality', color: '#ef4444', bg: '#fee2e2' }
    };
    const config = qualityConfig[qualityScore] || qualityConfig.GREEN;
    return (
      <span className="quality-badge" style={{ background: config.bg, color: config.color }}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="templates-container">
      <div className="templates-header">
        <div className="header-content">
          <h1>Message Templates</h1>
          <p>Create and manage pre-approved message templates for WhatsApp Business</p>
        </div>
        <button className="btn-create-template" onClick={handleCreateTemplate}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Create Template
        </button>
      </div>

      <div className="templates-toolbar">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search templates by name or content..."
            value={filters.search || ''}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Category:</label>
          <select
            value={filters.category || 'all'}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="filter-select"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category === 'all' ? 'All Categories' : category}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status || 'all'}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="filter-select"
          >
            {statuses.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Status' : status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="templates-stats">
          <span className="stat-item">
            Total: <strong>{templates.length}</strong>
          </span>
          <span className="stat-item">
            Approved: <strong>{templates.filter(t => t.status === 'APPROVED').length}</strong>
          </span>
          <span className="stat-item">
            Pending: <strong>{templates.filter(t => t.status === 'PENDING').length}</strong>
          </span>
        </div>
      </div>

      <div className="templates-grid">
        {loading ? (
          <div className="empty-state">
            <div className="spinner"></div>
            <h3>Loading templates...</h3>
          </div>
        ) : error ? (
          <div className="empty-state">
            <div className="empty-icon">⚠️</div>
            <h3>Error loading templates</h3>
            <p>{typeof error === 'object' ? error.message || JSON.stringify(error) : error}</p>
            <button className="btn-create-first" onClick={() => dispatch(fetchTemplates())}>
              Retry
            </button>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <h3>No templates found</h3>
            <p>Create your first message template to get started</p>
            <button className="btn-create-first" onClick={handleCreateTemplate}>
              Create Template
            </button>
          </div>
        ) : (
          filteredTemplates.map(template => {
            // Debug: Log template data to see what we're receiving
            if (template.components?.header?.type === 'IMAGE' || template.components?.header?.type === 'VIDEO' || template.components?.header?.type === 'DOCUMENT') {
              console.log('[Templates] Media template received:', {
                name: template.displayName,
                headerType: template.components.header.type,
                mediaUrl: template.components.header.mediaUrl,
                mediaPath: template.components.header.mediaPath,
                mediaFileName: template.components.header.mediaFileName,
                headerObject: template.components.header
              });
            }
            
            // Generate example text with variables replaced
            let exampleBodyText = '';
            if (template.components?.body?.text && template.components?.body?.example?.body_text?.[0]) {
              exampleBodyText = template.components.body.text;
              const exampleValues = template.components.body.example.body_text[0];
              if (Array.isArray(exampleValues)) {
                exampleValues.forEach((val, idx) => {
                  exampleBodyText = exampleBodyText.replace(new RegExp(`\\{\\{${idx + 1}\\}\\}`, 'g'), val);
                });
              }
            } else if (template.components?.body?.text) {
              exampleBodyText = replaceVariablesWithNames(template.components.body.text, template.variables);
            }

            return (
              <div key={template.id} className="template-card">
                {/* Card Header */}
                <div className="template-card-header">
                  <div className="template-card-title-section">
                    <h3 className="template-card-name">{template.displayName || template.name}</h3>
                    <div className="template-card-badges">
                      <span className="category-badge">{template.category}</span>
                      <span className="language-badge">{template.language ? template.language.toUpperCase() : 'EN'}</span>
                      {template.type === 'MEDIA' && (
                        <span className="media-type-badge" title="Media Template">
                          🖼️ {template.headerType === 'IMAGE' ? 'Image' : template.headerType === 'VIDEO' ? 'Video' : 'Document'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="template-card-status">
                    {getStatusBadge(template.status, template.rejectionReason)}
                    {getQualityBadge(template.qualityScore)}
                  </div>
                </div>

                {/* WhatsApp Message Preview in Card */}
                <div className="template-card-preview">
                  <div className="whatsapp-message-preview-card">
                    <div className="whatsapp-message-container-card">
                      {/* Header (if exists) */}
                      {template.components?.header && (
                        <div className="whatsapp-message-header-card">
                          {template.components.header.type === 'TEXT' && (
                            <div className="whatsapp-header-text-card">
                              {template.components.header.text}
                            </div>
                          )}
                          {template.components.header.type === 'IMAGE' && (
                            <div className="whatsapp-header-media-card">
                              {template.components?.header?.mediaUrl ? (
                                <img 
                                  src={process.env.REACT_APP_BASE_URL + template.components.header.mediaUrl} 
                                  alt="Header Media" 
                                  className="header-media-preview"
                                  loading="lazy"
                                  onError={(e) => {
                                    console.error('[Templates] Image load failed:', process.env.REACT_APP_BASE_URL + template.components.header.mediaUrl);
                                    e.target.style.display = 'none';
                                  }}
                                  onLoad={() => console.log('[Templates] Image loaded successfully:', process.env.REACT_APP_BASE_URL + template.components.header.mediaUrl)}
                                />
                              ) : (
                                <div className="media-placeholder-card image-placeholder-card">
                                  {template.components?.header?.mediaPath && (
                                    <div style={{ fontSize: '10px', color: '#666', marginBottom: '5px' }}>
                                      Path: {template.components.header.mediaPath}
                                    </div>
                                  )}
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                    <polyline points="21 15 16 10 5 21"></polyline>
                                  </svg>
                                  <span>🖼️ Image</span>
                                  {template.components?.header?.mediaFileName && (
                                    <div className="media-filename">{template.components.header.mediaFileName}</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          {template.components.header.type === 'VIDEO' && (
                            <div className="whatsapp-header-media-card">
                              {template.components?.header?.mediaUrl ? (
                                <video 
                                  src={process.env.REACT_APP_BASE_URL + template.components.header.mediaUrl}
                                  alt="Header Video"
                                  className="header-media-preview"
                                  controls
                                  preload="none"
                                  style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="media-placeholder-card video-placeholder-card">
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polygon points="23 7 16 12 23 17 23 7"></polygon>
                                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                                  </svg>
                                  <span>🎬 Video</span>
                                  {template.components?.header?.mediaFileName && (
                                    <div className="media-filename">{template.components.header.mediaFileName}</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                         
                          {template.components.header.type === 'DOCUMENT' && (
                            <div className="whatsapp-header-media-card">
                              {template.components?.header?.mediaUrl ? (
                                <div className="header-document-preview">
                                  <a 
                                    href={process.env.REACT_APP_BASE_URL + template.components.header.mediaUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="document-link"
                                  >
                                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                      <polyline points="14 2 14 8 20 8"></polyline>
                                      <line x1="16" y1="13" x2="8" y2="13"></line>
                                      <line x1="16" y1="17" x2="8" y2="17"></line>
                                      <polyline points="10 9 9 9 8 9"></polyline>
                                    </svg>
                                    <span>{template.components?.header?.mediaFileName || 'Download'}</span>
                                  </a>
                                </div>
                              ) : (
                                <div className="media-placeholder-card document-placeholder-card">
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                    <polyline points="10 9 9 9 8 9"></polyline>
                                  </svg>
                                  <span>📄 Document</span>
                                  {template.components?.header?.mediaFileName && (
                                    <div className="media-filename">{template.components.header.mediaFileName}</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Body Message */}
                      <div className="whatsapp-message-bubble-card">
                        <div className="whatsapp-message-body-card">
                          {exampleBodyText || replaceVariablesWithNames(template.components?.body?.text || '', template.variables)}
                        </div>
                        {template.components?.footer?.text && (
                          <div className="whatsapp-message-footer-card">
                            {template.components.footer.text}
                          </div>
                        )}
                        <div className="whatsapp-message-time-card">
                          <span>Template</span>
                          <svg width="10" height="10" viewBox="0 0 16 15" fill="currentColor">
                            <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.541l1.32 1.266a.32.32 0 0 0 .484-.034l6.272-8.525a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185a.32.32 0 0 0 .484-.033l6.272-8.525a.365.365 0 0 0-.063-.51z"/>
                          </svg>
                        </div>
                      </div>

                      {/* Buttons */}
                      {template.components?.buttons && template.components.buttons.length > 0 && (
                        <div className="whatsapp-message-buttons-card">
                          {template.components.buttons.map((btn, idx) => (
                            <div key={idx} className="whatsapp-button-card">
                              {btn.type === 'QUICK_REPLY' && (
                                <span className="button-text-card">{btn.text}</span>
                              )}
                              {btn.type === 'URL' && (
                                <span className="button-text-card">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                    <polyline points="15 3 21 3 21 9"></polyline>
                                    <line x1="10" y1="14" x2="21" y2="3"></line>
                                  </svg>
                                  {btn.text}
                                </span>
                              )}
                              {btn.type === 'PHONE_NUMBER' && (
                                <span className="button-text-card">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                  </svg>
                                  {btn.text}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="template-card-footer">
                  {template.variables && template.variables.length > 0 && (
                    <div className="template-card-variables">
                      <span className="variables-label-card">Variables:</span>
                      <div className="variables-list-card">
                        {template.variables.map((variable, index) => (
                          <span key={index} className="variable-tag-card">
                            {typeof variable === 'object' ? (variable.displayText || variable.name || `var${index + 1}`) : variable}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="template-card-actions">
                    <div className="template-card-dates">
                      {template.approvedAt && (
                        <span className="date-text">{formatDate(template.approvedAt)}</span>
                      )}
                    </div>
                    <div className="template-card-buttons">
                      <button
                        className="action-btn-card view-btn-card"
                        onClick={() => handleViewTemplate(template)}
                        title="View Template"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>
                    
                      
                    
                      <button
                        className="action-btn-card delete-btn-card"
                        onClick={() => handleDeleteTemplate(template)}
                        title="Delete Template"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <TemplateModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleTemplateSave}
        template={editingTemplate}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        customerName={templateToDelete?.name || ''}
        type="template"
      />

      {/* Template View Modal */}
      {isViewModalOpen && viewingTemplate && (
        <div className="modal-overlay-template" onClick={() => setIsViewModalOpen(false)}>
          <div className="modal-content-template" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-template">
              <h2>Template Details: {viewingTemplate.name}</h2>
              <button 
                className="close-btn-template"
                onClick={() => setIsViewModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body-template">
              {/* Template Preview */}
              <div className="template-preview-section">
                <h3>Preview</h3>
                <div className="whatsapp-message-preview">
                  {/* Header */}
                  {viewingTemplate.components?.header && (
                    <div className="whatsapp-message-header-preview">
                      {viewingTemplate.components.header.type === 'TEXT' && (
                        <div className="header-text-preview">
                          {viewingTemplate.components.header.text}
                        </div>
                      )}
                      {viewingTemplate.components.header.type === 'IMAGE' && (
                        <div className="header-image-preview">
                          🖼️ IMAGE Header {viewingTemplate.components.header.mediaFileName && `(${viewingTemplate.components.header.mediaFileName})`}
                        </div>
                      )}
                      {viewingTemplate.components.header.type === 'DOCUMENT' && (
                        <div className="header-document-preview">
                          📄 DOCUMENT Header {viewingTemplate.components.header.mediaFileName && `(${viewingTemplate.components.header.mediaFileName})`}
                        </div>
                      )}
                      {viewingTemplate.components.header.type === 'VIDEO' && (
                        <div className="header-video-preview">
                          🎬 VIDEO Header {viewingTemplate.components.header.mediaFileName && `(${viewingTemplate.components.header.mediaFileName})`}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Body */}
                  <div className="whatsapp-message-body-preview">
                    {viewingTemplate.components?.body?.text || 'No body text'}
                  </div>

                  {/* Footer */}
                  {viewingTemplate.components?.footer && (
                    <div className="whatsapp-message-footer-preview">
                      {viewingTemplate.components.footer.text}
                    </div>
                  )}

                  {/* Buttons */}
                  {viewingTemplate.components?.buttons && viewingTemplate.components.buttons.length > 0 && (
                    <div className="whatsapp-message-buttons-preview">
                      {viewingTemplate.components.buttons.map((btn, idx) => (
                        <div key={idx} className="whatsapp-button-preview">
                          {btn.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Template Metadata */}
              <div className="template-metadata-section">
                <h3>Metadata</h3>
                <div className="metadata-grid">
                  <div className="metadata-item">
                    <label>Status:</label>
                    <span className={`status-badge status-${viewingTemplate.status?.toLowerCase() || 'unknown'}`}>
                      {viewingTemplate.status || 'Unknown'}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <label>Language:</label>
                    <span>{viewingTemplate.language || 'Not set'}</span>
                  </div>
                  <div className="metadata-item">
                    <label>Created:</label>
                    <span>{new Date(viewingTemplate.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="metadata-item">
                    <label>Category:</label>
                    <span>{viewingTemplate.category || 'Not set'}</span>
                  </div>
                  {viewingTemplate.wabaId && (
                    <div className="metadata-item">
                      <label>WABA ID:</label>
                      <span className="monospace">{viewingTemplate.wabaId}</span>
                    </div>
                  )}
                  {viewingTemplate.metaTemplateId && (
                    <div className="metadata-item">
                      <label>Meta ID:</label>
                      <span className="monospace">{viewingTemplate.metaTemplateId}</span>
                    </div>
                  )}
                  {viewingTemplate.namespace && (
                    <div className="metadata-item">
                      <label>Namespace:</label>
                      <span className="monospace">{viewingTemplate.namespace}</span>
                    </div>
                  )}
                  {viewingTemplate.qualityScore && (
                    <div className="metadata-item">
                      <label>Quality Score:</label>
                      <span>{viewingTemplate.qualityScore}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Template Variables */}
              {viewingTemplate.variables && viewingTemplate.variables.length > 0 && (
                <div className="template-variables-section">
                  <h3>Variables</h3>
                  <div className="variables-list-view">
                    {viewingTemplate.variables.map((variable, index) => (
                      <div key={index} className="variable-item-view">
                        <span className="variable-name">{`{{${index + 1}}}`}</span>
                        <span className="variable-type">{variable || 'Variable'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer-template">
              <button
                className="btn-modal-secondary"
                onClick={() => setIsViewModalOpen(false)}
              >
                Close
              </button>
              <button
                className="btn-modal-primary"
                onClick={() => {
                  handleSendTestClick(viewingTemplate);
                }}
              >
                📤 Send Test
              </button>
              <button
                className="btn-modal-primary"
                onClick={() => {
                  handleSyncTemplate(viewingTemplate);
                  setIsViewModalOpen(false);
                }}
              >
                🔄 Sync
              </button>
              <button
                className="btn-modal-primary"
                onClick={() => {
                  handleEditTemplate(viewingTemplate);
                  setIsViewModalOpen(false);
                }}
              >
                ✏️ Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Test Modal */}
      {isSendTestModalOpen && viewingTemplate && (
        <div className="modal-overlay-sendtest" onClick={() => setIsSendTestModalOpen(false)}>
          <div className="modal-content-sendtest" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-sendtest">
              <h2>Send Test Message</h2>
              <button 
                className="close-btn-sendtest"
                onClick={() => setIsSendTestModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body-sendtest">
              <p className="sendtest-info">
                Send a test message using template: <strong>{viewingTemplate.name}</strong>
              </p>
              
              <div className="form-group-sendtest">
                <label htmlFor="testPhoneNumber">Recipient Phone Number:</label>
                <PhoneInput
                  country={'in'}
                  value={testPhoneNumber}
                  onChange={(phone) => setTestPhoneNumber(phone)}
                  style={{ marginLeft: '50px' }}
                  inputProps={{
                    id: 'testPhoneNumber',
                    disabled: loading,
                    placeholder: '+1 (555) 123-4567',
                    className: 'input-sendtest'
                  }}
                  containerClass="phone-input-container-sendtest"
                  inputClass="phone-input-field-sendtest"
                  buttonClass="phone-input-button-sendtest"
                  dropdownClass="phone-input-dropdown-sendtest"
                  preferredCountries={['us', 'gb', 'ca', 'au', 'in']}
                  enableAreaCodes={true}
                  countryCodeEditable={false}
                />
                <small className="help-text">
                  Enter your phone number (country code will be auto-detected)
                </small>
              </div>

              {/* Template Preview in Modal */}
              <div className="template-preview-sendtest">
                <h4>Message Preview:</h4>
                <div className="whatsapp-message-sendtest">
                  {viewingTemplate.components?.header && (
                    <div className="preview-section">
                      <strong>Header:</strong> 
                      {viewingTemplate.components.header.type === 'TEXT' && (
                        <span> {viewingTemplate.components.header.text}</span>
                      )}
                      {viewingTemplate.components.header.type === 'IMAGE' && (
                        <span> 🖼️ IMAGE {viewingTemplate.components.header.mediaFileName ? `(${viewingTemplate.components.header.mediaFileName})` : ''}</span>
                      )}
                      {viewingTemplate.components.header.type === 'VIDEO' && (
                        <span> 🎬 VIDEO {viewingTemplate.components.header.mediaFileName ? `(${viewingTemplate.components.header.mediaFileName})` : ''}</span>
                      )}
                      {viewingTemplate.components.header.type === 'DOCUMENT' && (
                        <span> 📄 DOCUMENT {viewingTemplate.components.header.mediaFileName ? `(${viewingTemplate.components.header.mediaFileName})` : ''}</span>
                      )}
                      {!viewingTemplate.components.header.type && (
                        <span> [Header]</span>
                      )}
                    </div>
                  )}
                  <div className="preview-section">
                    <strong>Message:</strong> {viewingTemplate.components?.body?.text || 'No body'}
                  </div>
                  {viewingTemplate.components?.footer && (
                    <div className="preview-section">
                      <strong>Footer:</strong> {viewingTemplate.components.footer.text}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer-sendtest">
              <button
                className="btn-modal-secondary"
                onClick={() => setIsSendTestModalOpen(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn-modal-primary"
                onClick={handleSendTestMessage}
                disabled={loading || !testPhoneNumber.trim()}
              >
                {loading ? '📤 Sending...' : '📤 Send Test'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Templates;

